import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../firebase";
import "../../styles/Viewvehicle.css";
import { useNavigate } from "react-router-dom";

function Viewvehicle() {
  const [vehicles, setVehicles] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [modalImage, setModalImage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Filter states
  const [filterType, setFilterType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();

  // Vehicle type detection
  const getVehicleTypeFromSeries = (vehicleNumber) => {
    if (!vehicleNumber) return "Unknown";

    const normalized = vehicleNumber.toUpperCase().replace(/[\s-]/g, "");

    const specificVehicles = {
      "ABJ3284": "Lake Three-wheeler",
      "DAF5019": "Lake Geep",
      "GW3509": "Lake Lorry",
      "PD8586": "Lake Crew Cab",
      "RA9999": "Tractor",
    };

    if (specificVehicles[normalized]) {
      return specificVehicles[normalized];
    }

    return "Other";
  };

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line
  }, []);

  const fetchVehicles = async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      let q;
      if (lastDoc) {
        q = query(
          collection(db, "vehicleScans"),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(10)
        );
      } else {
        q = query(
          collection(db, "vehicleScans"),
          orderBy("createdAt", "desc"),
          limit(10)
        );
      }

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const newData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter duplicates
        setVehicles((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueData = newData.filter((item) => !existingIds.has(item.id));
          return [...prev, ...uniqueData];
        });

        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);

        // Check if there are more documents
        if (snapshot.docs.length < 10) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }

    setLoading(false);
  };

  // Delete function
  const handleDelete = async (vehicleId) => {
    try {
      await deleteDoc(doc(db, "vehicleScans", vehicleId));
      
      // Remove from local state
      setVehicles((prev) => prev.filter((vehicle) => vehicle.id !== vehicleId));
      setDeleteConfirm(null);
      
      alert("Vehicle record deleted successfully!");
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      alert("Failed to delete vehicle record. Please try again.");
    }
  };

  // Filter vehicles based on type and date range
  const filteredVehicles = vehicles.filter((vehicle) => {
    // Only Centauria Lake
    if (!vehicle.hotelName || vehicle.hotelName.toLowerCase() !== "centauria lake") return false;

    // Vehicle type filter
    const type = getVehicleTypeFromSeries(vehicle.vehicleNumber);
    if (filterType && type !== filterType) return false;

    // Date range filter
    const createdDate = vehicle.createdAt?.toDate ? vehicle.createdAt.toDate() : null;
    if (createdDate) {
      if (startDate && createdDate < new Date(startDate)) return false;
      if (endDate && createdDate > new Date(endDate + "T23:59:59")) return false;
    }

    return true;
  });

  return (
    <div className="viewvehicle-container">
      <h2>Captured Vehicle Records</h2>

      <button onClick={() => navigate("/savs")} className="history-btn">
        Vehicles Summary
      </button>

      <button onClick={() => navigate("/driveradmin")} className="history-btn">
        Drivers Logs
      </button>

      {/* Filter Section */}
      <div className="filter-section">
        <label>
          Vehicle Type:{" "}
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All</option>
            <option value="Car">Car</option>
            <option value="Lake Lorry">Lorry</option>
            <option value="Motorcycle">Motorcycle</option>
            <option value="Bus">Bus</option>
            <option value="Lake Three-wheeler">Three-wheeler</option>
            <option value="Lake Geep">Geep</option>
            <option value="Tractor">Tractor</option>
            <option value="Heavy Vehicle">Heavy Vehicle</option>
            <option value="Other">Other</option>
          </select>
        </label>

        <label>
          From:{" "}
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>

        <label>
          To:{" "}
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>

      <div className="vehicle-table-wrapper">
        {filteredVehicles.length === 0 && !loading ? (
          <p>No records found.</p>
        ) : (
          <table className="vehicle-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Vehicle Type</th>
                <th>Vehicle Number</th>
                <th>Direction</th>
                <th>Purpose</th>
                <th>Meter Reading</th>
                <th>Uploaded Image</th>
                <th>User</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(
                filteredVehicles.reduce((groups, vehicle) => {
                  const hotel = vehicle.hotelName || "Unknown Hotel";
                  if (!groups[hotel]) groups[hotel] = [];
                  groups[hotel].push(vehicle);
                  return groups;
                }, {})
              ).map(([hotel, hotelVehicles]) => (
                <React.Fragment key={hotel}>
                  {/* Hotel Heading */}
                  <tr className="hotel-row">
                    <td colSpan="9" style={{ textAlign: "center", fontWeight: "bold" }}>
                      {hotel}
                    </td>
                  </tr>

                  {/* Vehicles under this hotel */}
                  {hotelVehicles.map((vehicle) => (
                    <tr key={vehicle.id}>
                      <td>
                        {vehicle.createdAt?.toDate
                          ? `${vehicle.createdAt.toDate().toLocaleDateString("en-GB")} ${vehicle.createdAt
                              .toDate()
                              .toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })}`
                          : "N/A"}
                      </td>
                      <td>{getVehicleTypeFromSeries(vehicle.vehicleNumber)}</td>
                      <td>{vehicle.vehicleNumber || "N/A"}</td>
                      <td>{vehicle.vehicleDirection || "N/A"}</td>
                      <td>{vehicle.purpose || "N/A"}</td>
                      <td>{vehicle.meter || "N/A"}</td>
                      <td>
                        <div className="thumbnail-row">
                          {vehicle.uploadedUrls && vehicle.uploadedUrls.length > 0 ? (
                            vehicle.uploadedUrls.map((url, idx) => (
                              <img
                                key={idx}
                                src={url}
                                alt={`Uploaded ${idx + 1}`}
                                className="vehicle-img"
                                loading="lazy"
                                onClick={() => setModalImage(url)}
                              />
                            ))
                          ) : (
                            "N/A"
                          )}
                        </div>
                      </td>
                      <td>
                        {vehicle.userEmail ? vehicle.userEmail.split("@")[0] : "N/A"}
                      </td>
                      <td>
                        <button
                          onClick={() => setDeleteConfirm(vehicle.id)}
                          className="delete-btn"
                          style={{
                            backgroundColor: "#dc3545",
                            color: "white",
                            padding: "5px 10px",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        <div className="load-more">
          {loading && <p>Loading...</p>}
          {!loading && hasMore && vehicles.length > 0 && (
            <button onClick={fetchVehicles} className="load-more-btn">
              Load More
            </button>
          )}
          {!hasMore && vehicles.length > 0 && <p>No more records</p>}
        </div>

        {/* Image Modal */}
        {modalImage && (
          <div className="image-modal" onClick={() => setModalImage(null)}>
            <img src={modalImage} alt="Large" className="modal-img" />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div
            className="delete-modal"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                maxWidth: "400px",
              }}
            >
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete this vehicle record?</p>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Yes, Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Viewvehicle;